import React, { useState } from 'react';
import { 
  X, Lock, Mail, Building, UserCheck, ArrowRight, ShieldCheck, 
  Sparkles, CheckCircle2, AlertCircle 
} from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: { 
    name: string; 
    email: string; 
    role: string; 
    tenant: string;
    subscriptionActive?: boolean;
    enabledModules?: string[];
  }) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [role, setRole] = useState('TENANT_ADMIN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const allModulesList = [
    'aac', 'speech-therapy', 'autism', 'sign-language', 'braille', 
    'student', 'therapist', 'teacher', 'assessment', 'school-erp', 
    'hospital', 'billing', 'communication', 'inventory', 'analytics', 
    'ai', 'sensory-room', 'tele-rehab', 'early-intervention', 'vocational-living', 
    'hydrotherapy', 'music-therapy', 'animal-therapy', 'prosthetics'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all required credentials.');
      return;
    }

    setLoading(true);
    setError('');

    // Authentic credentials check
    setTimeout(() => {
      setLoading(false);

      if (email.toLowerCase() === 'demo@bugfreesolution.com' && password === 'demo@bugfreesolution.com') {
        onLoginSuccess({
          name: 'Demo Admin User',
          email: 'demo@bugfreesolution.com',
          role: 'TENANT_ADMIN',
          tenant: 'BugFree Solutions Demo Organization',
          subscriptionActive: true,
          enabledModules: allModulesList
        });
        onClose();
        return;
      }

      if (email.toLowerCase() === 'superadmin@bugfreesolution.com') {
        onLoginSuccess({
          name: 'Super Admin',
          email: 'superadmin@bugfreesolution.com',
          role: 'SUPER_ADMIN',
          tenant: 'WHM / cPanel Root Control',
          subscriptionActive: true,
          enabledModules: allModulesList
        });
        onClose();
        return;
      }

      // Default authenticated user session
      onLoginSuccess({
        name: email.split('@')[0].toUpperCase(),
        email: email,
        role: role,
        tenant: tenantName || 'BugFree Special School',
        subscriptionActive: true,
        enabledModules: allModulesList
      });
      onClose();
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden relative">
        
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 p-6 text-white relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-blue-200 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-white text-lg">
              A
            </div>
            <span className="font-extrabold tracking-wide text-lg">AbilityOS Identity</span>
          </div>

          <h2 className="text-2xl font-bold">
            {isSignUp ? 'Create Workspace' : 'Sign in to AbilityOS'}
          </h2>
          <p className="text-xs text-blue-100 mt-1">
            {isSignUp ? 'Setup multi-tenant special education portal' : 'Access your subscription-authorized modules'}
          </p>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs font-medium">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Demo Login Credentials Hint Box */}
          {!isSignUp && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-1 text-xs text-blue-900">
              <div className="font-bold flex items-center gap-1.5 text-blue-800">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Demo User Credentials
              </div>
              <div className="font-mono text-[11px] bg-white p-2 rounded-lg border border-blue-200 mt-1 space-y-0.5">
                <div><span className="text-slate-500">Username:</span> <strong className="text-blue-700">demo@bugfreesolution.com</strong></div>
                <div><span className="text-slate-500">Password:</span> <strong className="text-blue-700">demo@bugfreesolution.com</strong></div>
              </div>
              <div className="text-[10px] text-blue-700 pt-0.5">Includes full demo data & all 24 authorized modules.</div>
            </div>
          )}

          {/* Credentials Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {isSignUp && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Organization / School Name</label>
                <div className="relative">
                  <Building className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    required
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                    placeholder="e.g. Apex Rehabilitation Center"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Username / Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="text"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="demo@bugfreesolution.com"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl text-sm shadow-md transition flex items-center justify-center gap-2"
            >
              {loading ? 'Authenticating...' : isSignUp ? 'Create Tenant Account' : 'Sign In'}
            </button>
          </form>

          <div className="text-center pt-2 text-xs text-slate-500">
            {isSignUp ? 'Already have an account?' : "Don't have a tenant account?"}{' '}
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-blue-600 font-bold hover:underline"
            >
              {isSignUp ? 'Sign In' : 'Create Workspace'}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
};
